import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, Printer, Save, X } from 'lucide-react';
import { uploadFile, srcForPath, compressImage } from '@/lib/uploads';

const EYEBROWS = ['PROPERTY PHOTOGRAPHS', 'SITE PHOTOGRAPHS', 'UNDER CONSTRUCTION'];
const SECTION_LABELS = ['Property Photographs', 'Site Photographs', 'Under Construction'];
const PERSON_LABELS = ['Owner Name', 'Proposed Purchaser', 'Applicant'];

const PHOTOS_PER_PAGE = 6;

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function A4Page({ photos, eyebrow, sectionLabel, personLabel, owner, refValue, scale = 0.4, printMode = false }) {
  const cls = printMode ? 'pe-a4-page-print' : 'pe-a4-page';
  const style = printMode ? {} : { transform: `scale(${scale})` };
  const countClass = `pe-count-${Math.min(6, Math.max(1, photos.length))}`;
  return (
    <div className={cls} style={style}>
      <div className="pe-heading-block">
        <div className="pe-heading-left">
          <div className="pe-heading-eyebrow">{eyebrow}</div>
          <div className="pe-heading-address">{sectionLabel}</div>
          <div className="pe-heading-owner">
            <span className="pe-heading-owner-prefix">{personLabel.toUpperCase()}: </span>
            {(owner || '').toUpperCase()}
          </div>
        </div>
        <div className="pe-heading-right">
          <div className="pe-ref-line">File Ref</div>
          <div className="pe-ref-value">{refValue || '—'}</div>
        </div>
      </div>
      <div className={`pe-photo-area ${countClass}`}>
        {photos.map((p, i) => (
          <div key={p || `photo-${i}`} className="pe-photo-frame pe-landscape" style={{ backgroundImage: `url(${srcForPath(p)})` }} />
        ))}
        {photos.length === 0 && <div className="pe-photo-empty">No photos added yet</div>}
      </div>
      <div className="pe-seal-block">
        <div className="pe-seal-content">
          <div className="pe-signature-line"></div>
          <div className="pe-stamp-name">Sai Kishore <span className="pe-stamp-name-suffix">B.E., M.E.</span></div>
          <div className="pe-stamp-title">Approved Valuer · Infinity Planners &amp; Designers</div>
          <div className="pe-stamp-detail">Karaikudi · Sivagangai</div>
        </div>
      </div>
    </div>
  );
}

export default function PhotoPageEditor({ work, onSave }) {
  const init = work.photo_page_data || {};
  const [eyebrow, setEyebrow] = useState(init.eyebrow || 'PROPERTY PHOTOGRAPHS');
  const [sectionLabel, setSectionLabel] = useState(init.section_label || 'Property Photographs');
  const [personLabel, setPersonLabel] = useState(init.person_label || 'Owner Name');
  const [owner, setOwner] = useState(init.owner || work.client_name || '');
  const [photos, setPhotos] = useState(init.photos || []);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  const refValue = work.file_ref || init.ref || '—';
  const pages = chunk(photos, PHOTOS_PER_PAGE);
  if (pages.length === 0) pages.push([]);

  // Sync default owner if file_ref or client_name updated
  useEffect(() => { if (!init.owner && work.client_name) setOwner(work.client_name); /* eslint-disable-next-line */ }, [work.client_name]);

  const onAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        const compressed = await compressImage(f, 1600, 0.78);
        const path = await uploadFile('photo-page-photos', compressed);
        paths.push(path);
      }
      setPhotos((p) => [...p, ...paths]);
      toast.success(`${paths.length} photo(s) compressed & uploaded`);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); e.target.value = ''; }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(work, {
        photo_page_data: {
          eyebrow, section_label: sectionLabel, person_label: personLabel,
          owner, photos, ref: refValue, stamp: 'BLUE',
        },
      });
      toast.success('Photo page saved');
    } finally { setSaving(false); }
  };

  const doPrint = () => { window.print(); };

  return (
    <div data-testid="photo-page-editor">
      <div className="pe-editor pe-no-print">
        <div className="pe-editor-toolbar">
          <div className="pe-editor-toolbar-info">
            <strong>{refValue}</strong> · {pages.length} page{pages.length > 1 ? 's' : ''} · {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pe-tb-btn" onClick={save} disabled={saving} data-testid="pe-save"><Save style={{ width: 12, height: 12, verticalAlign: -2 }} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="pe-tb-btn print" onClick={doPrint} data-testid="pe-print"><Printer style={{ width: 12, height: 12, verticalAlign: -2 }} /> Print / PDF</button>
          </div>
        </div>
        <div className="pe-editor-body">
          <div className="field-row-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div><div className="pe-field-lbl">Eyebrow Heading</div>
              <select className="pe-field-input" value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} data-testid="pe-eyebrow">
                {EYEBROWS.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div><div className="pe-field-lbl">Section Label</div>
              <select className="pe-field-input" value={sectionLabel} onChange={(e) => setSectionLabel(e.target.value)} data-testid="pe-section">
                {SECTION_LABELS.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div><div className="pe-field-lbl">Person Label</div>
              <select className="pe-field-input" value={personLabel} onChange={(e) => setPersonLabel(e.target.value)} data-testid="pe-person">
                {PERSON_LABELS.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><div className="pe-field-lbl">Owner Name</div>
              <input className="pe-field-input" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="pe-owner" />
            </div>
            <div><div className="pe-field-lbl">File Reference (auto)</div>
              <input className="pe-field-input" value={refValue} disabled style={{ background: '#fafaf9', color: '#78716c', fontWeight: 700 }} data-testid="pe-ref" />
            </div>
          </div>
          <label className="pe-upload-box" data-testid="pe-upload">
            <div className="pe-upload-box-icon"><Upload style={{ width: 22, height: 22 }} /></div>
            <div className="pe-upload-box-title">{busy ? 'Compressing & uploading…' : 'Click to add property photos'}</div>
            <div className="pe-upload-box-hint">Auto-compressed to 1600px · max 6MB · multiple at once</div>
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onAdd} disabled={busy} />
          </label>
          {photos.length > 0 && (
            <div>
              <div className="pe-field-lbl">Photos ({photos.length}) · {PHOTOS_PER_PAGE} per A4 page</div>
              <div className="pe-thumbs">
                {photos.map((p, i) => (
                  <div key={p || `thumb-${i}`} className="pe-thumb" style={{ backgroundImage: `url(${srcForPath(p)})` }} data-testid={`pe-thumb-${i}`}>
                    <button type="button" className="pe-del" onClick={() => removePhoto(i)}><X style={{ width: 12, height: 12 }} /></button>
                  </div>
                ))}
              </div>
              <div className="pe-thumb-count">Tap × to remove · drag to reorder coming soon</div>
            </div>
          )}
        </div>
      </div>

      {/* Live A4 preview (scaled) — hidden in print */}
      <div className="pe-no-print">
        {pages.map((pgPhotos, idx) => (
          <div key={`pg-${pgPhotos[0] || 'empty'}-${idx}`} className="pe-a4-wrap-shadow">
            <div className="pe-demo-label">Page {idx + 1} of {pages.length} · Preview (40%)</div>
            <div className="pe-a4-page-scaler" style={{ height: `${297 * 0.4 + 10}mm` }}>
              <A4Page photos={pgPhotos} eyebrow={eyebrow} sectionLabel={sectionLabel} personLabel={personLabel} owner={owner} refValue={refValue} scale={0.4} />
            </div>
          </div>
        ))}
      </div>

      {/* Full-scale print pages — visible only when printing */}
      <div ref={printRef} id="pe-print-pages" className="pe-print-only">
        {pages.map((pgPhotos, idx) => (
          <A4Page key={`print-${pgPhotos[0] || 'empty'}-${idx}`} photos={pgPhotos} eyebrow={eyebrow} sectionLabel={sectionLabel} personLabel={personLabel} owner={owner} refValue={refValue} printMode />
        ))}
      </div>
    </div>
  );
}
